<template>
  <div
    ref="containerRef"
    class="flex flex-col"
  >
    <textarea
      ref="inputRef"
      v-bind="$attrs"
      v-model="inputValue"
      :name="name"
    />

    <span
      v-if="error"
      ref="errorRef"
      class="text-red-600 text-sm"
    >{{ error }}</span>
  </div>
</template>

<script setup lang="ts">
import { useErrorVisibility } from '@/composables/useErrorVisibility';
import { useErrorIndicatorStore } from '@/store/errorIndicator';
import { nextTick, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    name: string;
    error?: string;
    enableFloatingError?: boolean;
  }>(),
  {
    error: undefined,
    enableFloatingError: false,
  },
);

const inputValue = defineModel<string>();
const inputRef = ref<HTMLTextAreaElement>();
const containerRef = ref<HTMLDivElement>();
const errorRef = ref<HTMLSpanElement>();
const errorIndicatorStore = useErrorIndicatorStore();
const { elementRef, isVisible, position, updateVisibility } = useErrorVisibility();

const focusInput = () => {
  inputRef.value?.focus();
};

const blur = () => {
  inputRef.value?.blur();
};

// Set the error element as the reference for visibility checking
const setErrorElementRef = () => {
  if(errorRef.value) {
    elementRef.value = errorRef.value;
  } else if(containerRef.value) {
    elementRef.value = containerRef.value;
  }
};

// Watch for error changes and handle floating indicator
watch(() => props.error, async (newError) => {
  if(!props.enableFloatingError) return;

  await nextTick();
  setErrorElementRef();
  updateVisibility();

  if(newError && !isVisible.value) {
    // Add floating error indicator based on input position
    errorIndicatorStore.addErrorIndicator(position.value);
  }
}, { immediate: true });

// Watch for visibility changes
watch(isVisible, (newIsVisible) => {
  if(!props.enableFloatingError || !props.error) {
    return;
  }

  if(newIsVisible) {
    // Remove floating indicator when error becomes visible
    errorIndicatorStore.clearIndicatorsByPosition(position.value);
  }
});

defineExpose({
  focusInput,
  blur,
});
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<style lang="css" scoped></style>
